import { ArrowSquareOut, Wrench } from '@phosphor-icons/react';
import { Button, Card, Stack, Text } from '@fragments-sdk/ui';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from 'convex/react';
import { api } from '@/convex';
import { useOrgContext } from '@/hooks/useOrgContext';
import { useProjectContext } from '@/hooks/useProjectContext';
import {
  CODE_CATEGORY_LABELS,
  GOVERNANCE_CODE_CATALOG,
  type GovernanceCodeCatalogEntry,
} from '@/lib/govern-code-catalog';
import s from './governance.module.scss';

const GROUPED_CODES = groupByCategory(GOVERNANCE_CODE_CATALOG);

/**
 * RuleCatalogPanel — the canonical FUI rule catalog, rendered inline under the
 * rules editor in the Governance "Rules" tab. Lifted from the standalone
 * /codes page (now removed); the open-count buttons still deep-link into the
 * findings list filtered by code.
 */
export function RuleCatalogPanel() {
  const navigate = useNavigate();
  const { org } = useOrgContext();
  const { selectedProjectId } = useProjectContext();
  const counts = useQuery(
    api.findings.getOpenCountsByCode,
    selectedProjectId ? { projectId: selectedProjectId } : 'skip',
  );
  const openByCode = counts?.byCode ?? {};

  function goToCodeFindings(code: string) {
    if (!org?.slug) return;
    navigate({
      to: `/${org.slug}/findings`,
      search: { status: 'open', code },
    } as unknown as Parameters<typeof navigate>[0]);
  }

  return (
    <Stack gap="md">
      <Stack gap="xs">
        <Text as="h2" size="sm" weight="semibold">
          Rule catalog
        </Text>
        <Text as="p" size="xs" color="secondary">
          Canonical FUI finding codes, rule ownership, and default governance
          behavior
        </Text>
      </Stack>

      <div className={s.catalogSummary}>
        <SummaryItem label="Rules" value={GOVERNANCE_CODE_CATALOG.length} />
        <SummaryItem
          label="Fixable"
          value={
            GOVERNANCE_CODE_CATALOG.filter((entry) => entry.fixAvailable).length
          }
        />
        <SummaryItem
          label="Families"
          value={new Set(GOVERNANCE_CODE_CATALOG.map((e) => e.family)).size}
        />
        <SummaryItem label="Open" value={counts?.total ?? 0} />
      </div>

      <Stack gap="lg">
        {GROUPED_CODES.map(({ category, entries }) => (
          <section key={category} className={s.codeCatalogSection}>
            <div className={s.codeCatalogHeader}>
              <Text as="h2" size="sm" weight="semibold">
                {CODE_CATEGORY_LABELS[category]}
              </Text>
              <Text as="span" size="xs" color="tertiary" weight="semibold">
                {entries.length} codes
              </Text>
            </div>
            <div className={s.codesTableShell}>
              <table className={s.codesTable}>
                <thead>
                  <tr>
                    <th>
                      <Text as="span" size="2xs" weight="bold">
                        Code
                      </Text>
                    </th>
                    <th>
                      <Text as="span" size="2xs" weight="bold">
                        Finding
                      </Text>
                    </th>
                    <th>
                      <Text as="span" size="2xs" weight="bold">
                        Rule
                      </Text>
                    </th>
                    <th>
                      <Text as="span" size="2xs" weight="bold">
                        Severity
                      </Text>
                    </th>
                    <th>
                      <Text as="span" size="2xs" weight="bold">
                        Open
                      </Text>
                    </th>
                    <th>
                      <Text as="span" size="2xs" weight="bold">
                        Lifecycle
                      </Text>
                    </th>
                    <th>
                      <Text as="span" size="2xs" weight="bold">
                        Fix
                      </Text>
                    </th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <tr key={entry.code}>
                      <td>
                        <Text as="span" size="2xs" className={s.codePill}>
                          {entry.code}
                        </Text>
                      </td>
                      <td>
                        <div className={s.codeTitleCell}>
                          <Text as="strong" size="xs" weight="semibold">
                            {entry.title}
                          </Text>
                          <Text as="span" size="2xs" color="tertiary">
                            {entry.familyLabel}
                          </Text>
                        </div>
                      </td>
                      <td>
                        <code className={s.ruleId}>{entry.ruleId}</code>
                      </td>
                      <td>
                        <Text
                          as="span"
                          size="2xs"
                          className={s.severityBadge}
                          data-severity={entry.defaultSeverity}
                        >
                          {entry.defaultSeverity}
                        </Text>
                      </td>
                      <td>
                        <Button
                          type="button"
                          variant="ghost"
                          size="xs"
                          className={s.codeCountButton}
                          disabled={!openByCode[entry.code]}
                          onClick={() => goToCodeFindings(entry.code)}
                        >
                          {openByCode[entry.code] ?? 0}
                        </Button>
                      </td>
                      <td>
                        <Text
                          as="span"
                          size="2xs"
                          className={s.lifecycleBadge}
                          data-lifecycle={entry.lifecycle}
                        >
                          {entry.lifecycle}
                        </Text>
                      </td>
                      <td>
                        <Text
                          as="span"
                          size="2xs"
                          className={s.fixAvailableBadge}
                          data-enabled={entry.fixAvailable || undefined}
                        >
                          {entry.fixAvailable ? (
                            <>
                              <Wrench size={12} />
                              Fixable
                            </>
                          ) : (
                            'Manual'
                          )}
                        </Text>
                      </td>
                      <td>
                        <a
                          href={entry.explainUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={s.tableIconLink}
                          aria-label={`Open docs for ${entry.code}`}
                          title={`Open docs for ${entry.code}`}
                        >
                          <ArrowSquareOut size={13} />
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))}
      </Stack>
    </Stack>
  );
}

function SummaryItem({ label, value }: { label: string; value: number }) {
  return (
    <Card
      as="div"
      variant="outlined"
      padding="none"
      className={s.catalogSummaryItem}
    >
      <Text as="span" size="2xs" color="tertiary" weight="semibold">
        {label}
      </Text>
      <Text as="strong" size="md" weight="bold">
        {value}
      </Text>
    </Card>
  );
}

function groupByCategory(codes: readonly GovernanceCodeCatalogEntry[]) {
  const map = new Map<
    GovernanceCodeCatalogEntry['category'],
    GovernanceCodeCatalogEntry[]
  >();
  for (const entry of codes) {
    const group = map.get(entry.category) ?? [];
    group.push(entry);
    map.set(entry.category, group);
  }
  return [...map.entries()].map(([category, entries]) => ({
    category,
    entries,
  }));
}
